import { newRequestId } from './weightService';
import { supabase } from '../supabase';

/**
 * Clones a program and all its weeks, days, blocks and items.
 *
 * HIDDEN UNTIL COMPLETE. The copy is a dozen requests, and any of them can fail. The new program
 * row is therefore created archived, and un-archived by the last request, after every part is in
 * place. A copy that fails part-way stays archived, and an archived program is shown in no list and
 * offered for no assignment (templateLibrary.js, assignablePrograms.js). Before this, a failed
 * "Clone" in the Programs Library left a part-built "Copy of ..." sitting among the coach's own
 * programs, ready to be assigned to a client with weeks, days or exercises missing.
 *
 * Removing the failed copy is tried as well, but it cannot be relied on: only a super_admin may
 * delete a program, so for a coach the row stays; and what broke the copy (a dropped connection)
 * usually breaks the removal too. Hidden is what makes it safe; removal is only tidying.
 *
 * @param {object} options
 * @param {string} options.programId        - Source program ID to clone
 * @param {string} options.newName          - Name for the cloned program
 * @param {string} options.userId           - Owner of the new program record
 * @param {boolean} [options.isClientCopy]  - Mark as a client copy (hidden from library)
 * @param {string} [options.sourceProgramId] - Original program ID (for client copies)
 * @returns {Promise<string>} New program ID
 */
export const cloneProgram = async ({ programId, newName, userId, isClientCopy = false, sourceProgramId = null }) => {
  // 1. Fetch source program + current user's auth UUID (for created_by)
  const [
    { data: source, error: srcErr },
    { data: { user: authUser }, error: authErr },
  ] = await Promise.all([
    supabase.from('programs').select('description, uses_blocks').eq('id', programId).single(),
    supabase.auth.getUser(),
  ]);
  if (srcErr) throw srcErr;

  // getUser() is a NETWORK call that validates the token, so it fails like any other request —
  // and on failure it resolves with user: null rather than throwing. This used to be written as
  // `created_by: authUser?.id ?? null`, which turned that failure into a program nobody owns:
  // the block_items RLS policy requires programs.created_by = auth.uid() for a coach, so an
  // unowned program is permanently uneditable by every coach, silently, forever. 34 client
  // copies were created that way between March and September 2026.
  //
  // Refusing to clone is strictly better than creating one that cannot be edited later.
  if (authErr || !authUser?.id) {
    throw new Error(
      'Could not confirm who you are signed in as, so the program was not created. ' +
      'Check your connection and try again.'
    );
  }

  // 2. Create new program record, archived: nothing shows it until step 9.
  const { data: newProgram, error: progErr } = await supabase
    .from('programs')
    .insert({
      name: newName,
      user_id: userId,
      created_by: authUser.id,
      description: source.description || null,
      is_client_copy: isClientCopy,
      source_program_id: sourceProgramId ? parseInt(sourceProgramId, 10) : null,
      uses_blocks: source.uses_blocks === true,
      archived_at: new Date().toISOString(),
    })
    .select('id')
    .single();
  if (progErr) throw progErr;

  try {
    await copyProgramTree(programId, newProgram.id);

    // 9. Every part is in place: show it. The row must really have been updated; a request that
    // "succeeds" on no rows would leave a complete program that nobody can see.
    const { data: shown, error: showErr } = await supabase
      .from('programs')
      .update({ archived_at: null })
      .eq('id', newProgram.id)
      .select('id');
    if (showErr) throw showErr;
    if ((shown?.length ?? 0) === 0) {
      throw new Error('The copy was made but could not be finished. Try again.');
    }
  } catch (err) {
    // Not awaited: the person waiting should hear about the failure now, not after another
    // handful of requests to a connection that has just failed. See HIDDEN UNTIL COMPLETE above.
    deleteClientCopyProgram(newProgram.id).catch(() => {});
    throw err;
  }

  return newProgram.id;
};

// Steps 3 to 8 of cloneProgram: copies every week, day, block and item of one program into another.
// Throws on the first request that fails, leaving whatever it had copied so far.
const copyProgramTree = async (programId, newProgramId) => {
  // 3. Fetch the whole source tree (weeks/days/blocks/items/legacy exercises)
  // in a handful of bulk queries, then bulk-insert each level — instead of
  // one round trip per row nested three loops deep (a 4-week/3-day/2-block
  // program was ~100+ sequential awaits, ~30s over the network; this is
  // ~12 round trips total regardless of program size).
  const { data: weeks, error: weeksErr } = await supabase
    .from('program_weeks')
    .select('id, name, week_number')
    .eq('program_id', programId)
    .order('week_number');
  if (weeksErr) throw weeksErr;
  if (!weeks || weeks.length === 0) return;

  const weekIds = weeks.map((w) => w.id);
  const { data: days, error: daysErr } = await supabase
    .from('program_days')
    .select('*')
    .in('week_id', weekIds)
    .order('day_number');
  if (daysErr) throw daysErr;

  const dayIds = (days || []).map((d) => d.id);
  let blocks = [];
  let legacyExercises = [];
  if (dayIds.length > 0) {
    const [{ data: blocksData, error: blocksErr }, { data: legacyData, error: legacyErr }] =
      await Promise.all([
        supabase
          .from('exercise_blocks')
          .select('id, block_order, block_type, set_category, category_params, rest_seconds, day_id')
          .in('day_id', dayIds)
          .order('block_order'),
        supabase
          .from('program_exercises')
          .select('*')
          .in('day_id', dayIds)
          .order('exercise_order'),
      ]);
    if (blocksErr) throw blocksErr;
    if (legacyErr) throw legacyErr;
    blocks = blocksData || [];
    legacyExercises = legacyData || [];
  }

  const blockIds = blocks.map((b) => b.id);
  let items = [];
  if (blockIds.length > 0) {
    const { data: itemsData, error: itemsErr } = await supabase
      .from('block_items')
      .select('*')
      .in('block_id', blockIds)
      .order('position');
    if (itemsErr) throw itemsErr;
    items = itemsData || [];
  }

  // 4. Bulk-insert weeks. A single multi-row INSERT ... RETURNING preserves
  // input order in Postgres, so newWeeks[i] corresponds to weeks[i] — used
  // below to build old-id -> new-id maps for the child levels.
  const { data: newWeeks, error: newWeeksErr } = await supabase
    .from('program_weeks')
    .insert(
      weeks.map((w) => ({ program_id: newProgramId, name: w.name, week_number: w.week_number }))
    )
    .select('id');
  if (newWeeksErr) throw newWeeksErr;
  const weekIdMap = new Map(weeks.map((w, i) => [w.id, newWeeks[i].id]));

  // 5. Bulk-insert days. Each source day's lasting identity (migration 086's slot_key) becomes a new one
  // in the copy, the same for every week's copy of that day, so a day and its twin never merge (Codex's
  // review 028). Read with '*' so this also works before 086 has run (no key: the database fills it).
  let dayIdMap = new Map();
  if (days && days.length > 0) {
    const newKeys = new Map();
    const keyFor = (k) => {
      if (!k) return undefined;
      if (!newKeys.has(k)) newKeys.set(k, newRequestId());
      return newKeys.get(k);
    };
    const { data: newDays, error: newDaysErr } = await supabase
      .from('program_days')
      .insert(
        days.map((d) => ({
          week_id: weekIdMap.get(d.week_id),
          name: d.name,
          day_number: d.day_number,
          day_name: d.day_name,
          ...(d.slot_key ? { slot_key: keyFor(d.slot_key) } : {}),
        }))
      )
      .select('id');
    if (newDaysErr) throw newDaysErr;
    dayIdMap = new Map(days.map((d, i) => [d.id, newDays[i].id]));
  }

  // 6. Bulk-insert exercise_blocks
  let blockIdMap = new Map();
  if (blocks.length > 0) {
    const { data: newBlocks, error: newBlocksErr } = await supabase
      .from('exercise_blocks')
      .insert(
        blocks.map((b) => ({
          day_id: dayIdMap.get(b.day_id),
          block_order: b.block_order,
          block_type: b.block_type,
          set_category: b.set_category,
          category_params: b.category_params,
          rest_seconds: b.rest_seconds,
        }))
      )
      .select('id');
    if (newBlocksErr) throw newBlocksErr;
    blockIdMap = new Map(blocks.map((b, i) => [b.id, newBlocks[i].id]));
  }

  // 7. Bulk-insert block_items
  if (items.length > 0) {
    const { error: itemsErr } = await supabase.from('block_items').insert(
      items.map(({ id: _id, block_id, ...rest }) => ({
        ...rest,
        block_id: blockIdMap.get(block_id),
      }))
    );
    if (itemsErr) throw itemsErr;
  }

  // 8. Bulk-insert legacy program_exercises
  if (legacyExercises.length > 0) {
    const { error: exErr } = await supabase.from('program_exercises').insert(
      legacyExercises.map(({ id: _id, day_id, ...rest }) => ({
        ...rest,
        day_id: dayIdMap.get(day_id),
      }))
    );
    if (exErr) throw exErr;
  }
};

/**
 * Deletes a client copy program and all its child records.
 * Only safe to call on is_client_copy=true programs, or on a copy cloneProgram has just made and
 * given to nobody.
 *
 * Best effort: no step is checked, and for anyone but a super_admin the last one (the program row
 * itself) is refused by the access rules without an error.
 */
export const deleteClientCopyProgram = async (programId) => {
  const { data: weeks } = await supabase
    .from('program_weeks')
    .select('id')
    .eq('program_id', programId);

  const weekIds = weeks?.map(w => w.id) || [];

  if (weekIds.length > 0) {
    const { data: days } = await supabase
      .from('program_days')
      .select('id')
      .in('week_id', weekIds);

    const dayIds = days?.map(d => d.id) || [];

    if (dayIds.length > 0) {
      const { data: blocks } = await supabase
        .from('exercise_blocks')
        .select('id')
        .in('day_id', dayIds);

      const blockIds = blocks?.map(b => b.id) || [];
      if (blockIds.length > 0) {
        await supabase.from('block_items').delete().in('block_id', blockIds);
        await supabase.from('exercise_blocks').delete().in('id', blockIds);
      }

      await supabase.from('program_exercises').delete().in('day_id', dayIds);
      await supabase.from('program_days').delete().in('id', dayIds);
    }

    await supabase.from('program_weeks').delete().in('id', weekIds);
  }

  await supabase.from('programs').delete().eq('id', programId);
};

/**
 * Puts aside a complete copy that was made for a client and then never given to them, because the
 * assignment that should have followed failed.
 *
 * Archived first: that is the step a coach is allowed to take, and the one that matters. Then
 * removed as far as the access rules allow. Never throws: it is called while an error is already
 * on its way to the coach, and must not replace it.
 */
export const discardUnassignedCopy = async (programId) => {
  try {
    await supabase
      .from('programs')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', programId)
      .eq('is_client_copy', true);
    await deleteClientCopyProgram(programId);
  } catch {
    // Nothing useful to do: the copy is unassigned either way.
  }
};
